/**
 * Teacher advanced pages — Gradebook, Diary note, Report-card remarks.
 * Course-landing/plan/resources and Paper generator wait for the course model.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, Modal, Select, Skeleton, Space, Table, Tag, Typography,
  message,
} from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type {
  CreateDiaryNoteRequest, DiaryNoteRow, ExamRemarkRow, GradebookCourseRow,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

/* ---------------- Gradebook ---------------- */

export function TeacherGradebookPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['teacher-gradebook', institution],
    queryFn: () => productApi.getTeacherGradebook(institution),
    enabled: !!institution,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data.teacher) return <Alert type="warning" showIcon message="Your login isn't linked to a staff record." />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Gradebook</Typography.Title>
      {q.data.courses.length === 0 ? (
        <Empty description="No courses assigned to you yet." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {q.data.courses.map((c) => <CourseCard key={`${c.subject_id}-${c.grade}-${c.section}`} c={c} institution={institution} />)}
        </div>
      )}
    </Space>
  );
}

function CourseCard({ c, institution }: { c: GradebookCourseRow; institution: string }) {
  return (
    <Card size="small">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b style={{ fontSize: 15 }}>{c.subject_name}</b>
        <Tag>{c.subject_code}</Tag>
        <Tag color="blue">{c.grade}{c.section ? `-${c.section}` : ''}</Tag>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: '#5b6478' }}>{c.student_count} student{c.student_count === 1 ? '' : 's'}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
        <Stat title="Attendance" value={c.attendance_percent !== null ? `${c.attendance_percent}%` : '—'}
          tone={c.attendance_percent !== null && c.attendance_percent < 85 ? 'warn' : 'default'} />
        <Stat title="Homework" value={String(c.homework_assigned)} />
        <Stat title="Missing" value={String(c.homework_missing)} tone={c.homework_missing > 0 ? 'warn' : 'ok'} />
        <Stat title="Last exam avg"
          value={c.latest_exam?.average_percentage !== undefined && c.latest_exam?.average_percentage !== null
            ? `${c.latest_exam.average_percentage}%` : '—'} />
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Link to={`/i/${institution}/teacher/class/${encodeURIComponent(c.grade)}/${encodeURIComponent(c.section)}`}>
          <Button size="small">Class roster</Button>
        </Link>
        <Link to={`/i/${institution}/admin/homework?mine=true`}>
          <Button size="small">Homework</Button>
        </Link>
        {c.latest_exam && (
          <span style={{ fontSize: 11, color: '#8a94a6', alignSelf: 'center' }}>
            latest: {c.latest_exam.exam_name}
          </span>
        )}
      </div>
    </Card>
  );
}

function Stat({ title, value, tone }: { title: string; value: string; tone?: 'ok' | 'warn' | 'default' }) {
  const color = tone === 'warn' ? '#a24040' : tone === 'ok' ? '#1c6442' : '#14213d';
  return (
    <div style={{ background: '#f7f7f5', borderRadius: 6, padding: '6px 10px' }}>
      <div style={{ fontSize: 10, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ---------------- Diary note ---------------- */

interface DiaryForm { grade: string; section: string; date: Dayjs; body: string; }

export function TeacherDiaryNewPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<DiaryForm>();
  const profile = useQuery({
    queryKey: ['teacher-profile', institution],
    queryFn: () => productApi.getTeacherProfile(institution),
    enabled: !!institution,
  });
  const list = useQuery({
    queryKey: ['diary', institution],
    queryFn: () => productApi.listDiaryNotes(institution),
    enabled: !!institution,
  });
  const m = useMutation({
    mutationFn: (input: CreateDiaryNoteRequest) => productApi.createDiaryNote(institution, input),
    onSuccess: () => {
      form.resetFields();
      void message.success('Committed');
      void list.refetch();
    },
  });
  const err = m.error instanceof ApiError
    ? ((m.error.body as { message?: string } | null)?.message ?? null)
    : m.error instanceof Error ? m.error.message : null;

  const mySections: Array<{ grade: string; section: string }> = useMemo(() => Array.from(new Map(
    (profile.data?.weekly_slots ?? []).map((s) => [`${s.grade}|${s.section}`, { grade: s.grade, section: s.section }]),
  ).values()), [profile.data]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Diary note</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          U-07 · dictate or type — you commit the wording. Notes appear on the section's diary feed.
        </Typography.Text>
      </div>
      <Card size="small">
        <Form form={form} layout="vertical"
          onFinish={(v) => m.mutate({
            grade: v.grade, section: v.section, date: v.date.format('YYYY-MM-DD'), body: v.body.trim(),
          })}
        >
          <Space size={8} align="baseline" wrap>
            <Form.Item name="grade" label="Grade" rules={[{ required: true }]}>
              <Select
                style={{ minWidth: 160 }}
                placeholder="Pick a section"
                options={mySections.map((s) => ({
                  value: s.grade,
                  label: `${s.grade}${s.section ? `-${s.section}` : ''}`,
                  section: s.section,
                }))}
                onChange={(_v, opt) => {
                  const section = Array.isArray(opt) ? '' : (opt as { section?: string })?.section;
                  if (section) form.setFieldsValue({ section });
                }}
              />
            </Form.Item>
            <Form.Item name="section" label="Section" rules={[{ required: true }]}>
              <Input style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
          </Space>
          <Form.Item name="body" label="Note" rules={[{ required: true, min: 1, max: 2000 }]}>
            <Input.TextArea rows={4} placeholder="What happened / what to remember for tomorrow" />
          </Form.Item>
          {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}
          <Button type="primary" htmlType="submit" loading={m.isPending} icon={<PlusOutlined />}>Commit</Button>
        </Form>
      </Card>

      <Card size="small" title="Recent notes">
        {list.isLoading || !list.data ? <Skeleton active paragraph={{ rows: 3 }} /> : list.data.rows.length === 0 ? (
          <Empty description="No diary notes yet." style={{ padding: 20 }} />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {list.data.rows.slice(0, 20).map((r) => <DiaryCard key={r.id} r={r} />)}
          </Space>
        )}
      </Card>

      <Button onClick={() => navigate(`/i/${institution}/teacher/profile`)}>Done</Button>
    </Space>
  );
}

function DiaryCard({ r }: { r: DiaryNoteRow }) {
  return (
    <div style={{ borderTop: '1px solid #f1f1f4', paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Tag color="blue">{r.grade}-{r.section}</Tag>
        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{r.date}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#8a94a6' }}>{r.teacher_name}</span>
      </div>
      <div style={{ marginTop: 4, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{r.body}</div>
    </div>
  );
}

/* ---------------- Report-card remarks ---------------- */

export function TeacherRemarksPage() {
  const { institution = '', exam = '' } = useParams<{ institution: string; exam: string }>();
  const qc = useQueryClient();
  const [grade, setGrade] = useState<string>('');
  const [section, setSection] = useState<string>('');
  const q = useQuery({
    queryKey: ['exam-remarks', institution, exam, grade, section],
    queryFn: () => productApi.getExamRemarks(institution, exam, { grade: grade || undefined, section: section || undefined }),
    enabled: !!institution && !!exam,
  });
  const save = useMutation({
    mutationFn: ({ student_id, body }: { student_id: string; body: string }) =>
      productApi.upsertExamRemark(institution, exam, { student_id, body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exam-remarks', institution, exam] });
      void message.success('Saved');
    },
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Remarks · {q.data.exam.name}</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Per-student report-card remarks. Kept short and personal.
        </Typography.Text>
      </div>
      <Space size={8}>
        <Input size="small" style={{ width: 100 }} placeholder="Grade" value={grade} onChange={(e) => setGrade(e.target.value)} />
        <Input size="small" style={{ width: 100 }} placeholder="Section" value={section} onChange={(e) => setSection(e.target.value)} />
      </Space>
      <Card size="small">
        {q.data.rows.length === 0 ? (
          <Empty description="No students in this scope." style={{ padding: 20 }} />
        ) : (
          <Table<ExamRemarkRow>
            size="small" pagination={{ pageSize: 20 }} rowKey="student_id"
            dataSource={q.data.rows}
            columns={[
              { title: 'Student', key: 's',
                render: (_, r) => <>
                  <b>{r.student_name}</b>
                  <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.admission_number}</div>
                </> },
              { title: 'Remark', dataIndex: 'body', key: 'b',
                render: (v: string | null) => v ? <span style={{ whiteSpace: 'pre-wrap', fontSize: 12.5 }}>{v}</span> : <Typography.Text type="secondary">—</Typography.Text> },
              { title: '', key: 'act', width: 80,
                render: (_, r) => <EditRemarkButton row={r} onSave={(body) => save.mutate({ student_id: r.student_id, body })} /> },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function EditRemarkButton({ row, onSave }: { row: ExamRemarkRow; onSave: (body: string) => void }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(row.body ?? '');
  return (
    <>
      <Button size="small" icon={<EditOutlined />} onClick={() => { setBody(row.body ?? ''); setOpen(true); }}>
        Edit
      </Button>
      <Modal open={open} title={`Remark · ${row.student_name}`}
        onCancel={() => setOpen(false)}
        okText="Save"
        onOk={() => { if (body.trim().length > 0) { onSave(body.trim()); setOpen(false); } }}>
        <Input.TextArea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Short, specific, kind. Points to next steps if possible." />
      </Modal>
    </>
  );
}
